import { useState, useEffect } from 'react';
import moment from 'moment';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Button
} from '@material-ui/core';
import { TrendingUp, TrendingDown, Refresh } from '@material-ui/icons';
import Chart from 'react-apexcharts';
import { BRAND_RED, BRAND_GREEN } from '../../management/charts/chartDefaults';
import { baseUrl } from '../../../utils/constants';
import { useConstantsContext } from '../../../context/constants_context';

const claimTypes = [
  { value: 'ClaimsByGroup', label: 'Claims By Province' },
  { value: 'ClaimsByAge', label: 'Claims By Age' },
  { value: 'ClaimsByType', label: 'Claims By Type' }
];

const baseLabelStyle = { colors: '#6b6b6b', fontSize: '12px' };

const today = new Date();
const defaultBeg = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
const defaultEnd = today.toISOString().slice(0, 10);

const OnsOffsCharts = ({ metric }) => {
  const { payCycles } = useConstantsContext();
  const [begDate, setBegDate] = useState(Number(sessionStorage.getItem('payCycleId')) - 1);
  const [endDate, setEndDate] = useState(Number(sessionStorage.getItem('payCycleId')));
  const [type, setType] = useState(claimTypes[0].value);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [data, setDataList] = useState([]);

  const Icon = metric?.icon;
  const isUp = metric?.trend === 'up';

  useEffect(() => {
    getChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('begDate in useEffect', begDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleRefresh = () => setRefreshKey((k) => k + 1);

  // const data = [
  //   { descriptions: 'BULAWAYO', totalClaimed: 5600, totalAwarded: 2100, noOfClaims: 12 },
  //   { descriptions: 'Harare', totalClaimed: 4600, totalAwarded: 1100, noOfClaims: 10 },
  //   { descriptions: 'Manicaland', totalClaimed: 5080, totalAwarded: 1000, noOfClaims: 11 }
  // ];

  const categories = data.map((d) => d.descriptions);
  const totalClaimed = data.map((d) => d.totalClaimed);
  const totalAwarded = data.map((d) => d.totalAwarded);
  const totalAwardedSum = data.reduce((s, d) => s + d.totalAwarded, 0);

  const options = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      fontFamily: 'inherit',
      animations: { easing: 'easeinout', speed: 600 }
    },
    colors: [BRAND_RED, BRAND_GREEN],
    stroke: { width: [0, 3], curve: 'straight' },
    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0, 1],
      formatter: (v) => `$${Number(v).toLocaleString()}`,
      style: { fontSize: '12px', fontWeight: 800, colors: ['#111827'] },
      offsetY: -8,
      background: { enabled: false },
      dropShadow: { enabled: false }
    },
    markers: { size: [0, 5], strokeWidth: 0 },
    grid: { borderColor: 'rgba(0,0,0,0.08)', strokeDashArray: 3 },
    xaxis: {
      categories,
      labels: { style: baseLabelStyle },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        style: baseLabelStyle,
        formatter: (v) => `$${Number(v).toLocaleString()}`
      }
    },
    legend: { labels: { colors: '#6b6b6b' } },
    tooltip: { theme: 'light', style: { fontSize: '13px' } }
  };

  const series = [
    { name: 'Total Claimed', type: 'bar', data: totalClaimed },
    { name: 'Total Awarded', type: 'line', data: totalAwarded }
  ];

  const getChartData = () => {
    setLoading(true);

    const url = `${baseUrl}/api/claims/GetClaimsStats?from=${begDate}&to=${endDate}&dateType=1&reportDesc=${type}`;

    fetch(url)
      .then((res) => res.json())
      .then(
        (result) => {
          setLoading(false);
          console.log('returned GetClaimsStats Summary list', result.result);
          setDataList(result.result ?? []); // fallback to empty array
        },
        (error) => {
          setLoading(false);
          // notifyPopup(`Error in getChartData!: ${error}`, 'error');
        }
      );
  };

  return (
    <Card elevation={2}>
      <CardContent sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            {Icon && <Icon sx={{ color: 'primary.contrastText', fontSize: 32 }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {metric?.title}
            </Typography>
            <Typography variant="h4" color="text.primary" sx={{ fontWeight: 700 }}>
              {`$${totalAwardedSum.toLocaleString()}`}
            </Typography>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ ml: { sm: 'auto' } }}
          >
            {metric?.trendValue && (
              <Chip
                icon={isUp ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                label={metric.trendValue}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: isUp ? 'success.main' : 'error.main',
                  color: '#fff',
                  '& .MuiChip-icon': { color: '#fff' }
                }}
              />
            )}
            <TextField
              select
              size="small"
              label="From Date"
              value={begDate}
              onChange={(e) => setBegDate(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem key="0" value="">
                Not Selected
              </MenuItem>
              {payCycles.slice(0, 12).map((item) => (
                <MenuItem key={item.payCycleId} value={item.payCycleId}>
                  {moment(item.payDate).isValid() ? moment(item.payDate).format('YYYY-MM-DD') : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="To Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem key="0" value="">
                Not Selected
              </MenuItem>
              {payCycles.slice(0, 12).map((item) => (
                <MenuItem key={item.payCycleId} value={item.payCycleId}>
                  {moment(item.payDate).isValid() ? moment(item.payDate).format('YYYY-MM-DD') : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {claimTypes.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={getChartData}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        <Typography variant="overline" color="text.secondary">
          Total claimed vs total awarded by region (USD)
        </Typography>
        <Box sx={{ width: '100%', height: { xs: 280, md: 380 }, mt: 1 }}>
          <Chart
            key={refreshKey}
            options={options}
            series={series}
            type="line"
            height="100%"
            width="100%"
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default OnsOffsCharts;
